/* eslint-disable no-unused-vars */
import React, {useState, useEffect} from 'react'
import Axios from 'axios'

import Jira from './components/util/Jira'
import Tracking from './components/util/Tracking'
import Editor from './components/editor/Editor'
import QAMenu from './components/UI/QAMenu'

import scoringObject from './scoring.js';

// import GlobalNav from './components/UI/GlobalNav'
import NavBar from './components/UI/NavBar'
import Menu from './components/UI/Menu'
import Linear from './components/sequencers/Linear'
import NonLinear from './components/sequencers/NonLinear'
import AudioController from './components/util/AudioController'
// import AudioVisualizer from './components/util/AudioVisualizer'

import useSurvey from './customHooks/useSurvey'

import globalData from './globals.json'
import appData from './app.json'
import config from './config.json'
import './sass/sass.js'

/**
 * Main App Class
 */
export default function App() {

  // Dev/QA cheats
  let href = document.location.href;
  const isDevEnvironment = !!(href.indexOf('localhost') > -1);
  const isLocalHost = href.indexOf('localhost') > -1;

  // Config
  // checks to see if you are looking at the dev environment and
  // will first use different starting variables if set
  const initialModule = isLocalHost ? config.initialLocation.devModule : config.initialLocation.module;
  const initialStep = isLocalHost ? config.initialLocation.devStep : config.initialLocation.step;

  // Course data, index states
  const [moduleIndex, setModuleIndex] = useState(initialModule);
  const [stepIndex, setStepIndex] = useState(initialStep);
  const [stepResetIndex, setStepResetIndex] = useState(0);
  const [contentData, setContentData] = useState(undefined);
  const [audioCollection, setAudioCollection] = useState(undefined);
  const [windowToggle, setWindowToggle] = useState(false);
  const [sharedData, setSharedData] = useState({});
  
  const [jira, setJira] = useState({
    file: ``,
    location: '',
    item: ``,
  });

  // Course progress state
  const [moduleProgress, setModuleProgress] = useState(appData.modules.map(() => {
    return {
      complete: false,
      recentStepIndex: 0,
      furthestStepIndex: 0,
      scoring: {},
      userData: {},
    }
  }));

  const [scoring, setScoring] = useState(scoringObject);

  //** Course Editor States
  const [editMode, setEditMode] = useState(false);
  const [force, setForce] = useState(false);

  const {handleSubmitSurvey} = useSurvey()

  const toggleForce = () => {
    setForce(!force);
  };
  //**

  /**
   * Controller
   * Catch all for global state manipulation that is passed to interactions
   * to give them access to global data and functions if needed.
   * Any data we know they will need should
   * ideally be it's own prop; use this as a backup
   */
  const controller = {

    // App state level getters and setters
    getStepIndex() {
      return stepIndex;
    },

    setStepIndex(i) {
      if (!contentData) return false;
      if (!contentData.steps) return false;
      if (i >= 0 && i < contentData.steps.length) {
        setStepIndex(i);
      } else {
        if (isDevEnvironment) console.log('invalid step ID passed');
      }
    },

    unsafeSetStepIndex(i) {
      if (isDevEnvironment) console.log('setting step index', i);
      setStepIndex(parseInt(i));
    },

    getModuleIndex() {
      return moduleIndex;
    },

    setModuleIndex(i) {
      if (isDevEnvironment) console.log('setModuleIndex()', i);
      let temp = parseInt(i);
      if (temp >= -1 && temp < appData.modules.length) {
        setModuleIndex(temp);
        setStepIndex(0);
        setContentData(undefined);
      } else {
        if (isDevEnvironment) console.log('invalid module ID passed');
      }
    },

    getContentURL() {
      return appData.modules[moduleIndex].source;
    },

    getContentData() {
      return contentData;
    },

    setContentData(data) {
      setContentData(data);
    },

    getModuleProgress() {
      return moduleProgress;
    },

    setModuleProgress(obj) {
      if (isDevEnvironment) console.log('updating progress state', obj);
      setModuleProgress(obj);
    },

    // Convenience functions
    compareSequencerName(str) {
      try {
        return appData.modules[moduleIndex].sequencer.toLowerCase() === str.toLowerCase();
      }
      catch (error) {
        return false;
      }
    },

    decrementStep() {
      if (stepIndex > 0) {
        controller.setStepIndex(stepIndex - 1);
      }
    },

    incrementStep() {
      if (!contentData.steps) return false;

      const nextStep = parseInt(stepIndex) + 1;
      if (nextStep < contentData.steps.length) {
        controller.setStepIndex(nextStep);
        if (moduleProgress[moduleIndex].furthestStepIndex < nextStep) {
          let temp = JSON.parse(JSON.stringify(moduleProgress));
          temp[moduleIndex].furthestStepIndex = nextStep;
          setModuleProgress(temp);
        }
      }
    },

    nonLinearStepComplete() {
      if (!contentData.steps) return false;
      // console.log('Need to set this back to the menu...');
    },

    stepComplete() {
      if (!contentData.steps) return false;
      
      // Check if current step has a custom nextStep property
      const currentStep = contentData.steps[stepIndex];
      if (currentStep && typeof currentStep.nextStep === 'number') {
        if (isDevEnvironment) console.log('custom next step:', currentStep.nextStep);
        controller.setStepIndex(currentStep.nextStep);
        return;
      }
      
      // Default behavior: move to next sequential step
      if (parseInt(stepIndex) + 1 < contentData.steps.length) {
        if (isDevEnvironment) console.log('next step');
        controller.incrementStep();
      } else {
        if (isDevEnvironment) console.log('next step does not exist, ending module', contentData.steps.length, stepIndex + 1);
        controller.onModuleComplete();
      }
    },

    resetStep() {
      setStepResetIndex(stepResetIndex + 1);
    },

    getCurrentStepData() {
      if (!contentData) return false;
      if (stepIndex >= 0 && stepIndex < contentData.steps.length) {
        return contentData.steps[stepIndex];
      }
    },

    goToMenu() {
      controller.setModuleIndex(-1);
      setStepIndex(0);
    },

    isDevEnvironment() {
      return isDevEnvironment;
    },

    toggleEditMode() {
      setEditMode(!editMode);
    },

    isEmptyString(myString) {
      let cleanString = myString.replace(/(\r\n|\n|\r)/gm, "");
      return cleanString === "<p></p>";
    },

    // Events
    onModuleComplete() {
      let progress = JSON.parse(JSON.stringify(moduleProgress));
      progress[moduleIndex].complete = true;
      setModuleProgress(progress);
      setContentData(undefined);

      let next = appData.modules[moduleIndex].nextModuleIndex;
      if (next === undefined) next = -1;
      setModuleIndex(next);
      setStepIndex(0);
    },

    forceRender() {
      toggleForce();
    }
  };

  /**
   * Jira Controller
   * sets JIRA locations
   */
  const jiraController = {
    setJiraFile(name) {
      setJira({
        file: name,
        location: jira.location,
        item: jira.item
      });
    },
    getJiraFile() {
      return jira.file;
    },
    setJiraLocation(name) {
      setJira({
          file: jira.file,
          location: name,
          item: jira.item
        }
      )
    },
    getJiraLocation() {
      return jira.location;
    },
    setJiraItem(name) {
      setJira({
        file: jira.file,
        location: jira.location,
        item: name
      })
    },
    getJiraItem() {
      return jira.item;
    }
  };

  /**
   * Audio controller
   */
  const audioController = {
    playAudio: (targetIdx) => {
      setAudioCollection(audioCollection.map((clip, idx) => {
        if (idx === targetIdx) {
          clip.isPlaying = true;
          clip.ended = false;
        }
        console.log(`Clip ${idx}`, clip);
        return clip;
      }));
    },

    pauseAudio: (targetIdx) => {
      setAudioCollection(audioCollection.map((clip, idx) => {
        if (idx === targetIdx) clip.isPlaying = false;
        return clip;
      }));
    },


    playAudioByName: (targetName) => { 
     
      setAudioCollection(audioCollection.map((clip, idx) => {
        let clipWasPlaying = clip.isPlaying
        if (clip.name === targetName) {
          clip.isPlaying = true;
          clip.ended = false;
        }
        if (clip.windowVolume && !clipWasPlaying) {
          clip.volume = clip.volume || 1.0
          setTimeout(() => {
            let audio = document.getElementById(`audio-instance-${clip.name}`);
            if (audio) {
              audio.volume = windowToggle ? clip.volume : clip.volume / 2
            }
          }, 50)
        }
        return clip;
      }));
      
    },
    playWindowAudioByName: (targetName) => {
      setAudioCollection(audioCollection.map((clip, idx) => {

      if (clip.name === targetName) {
        clip.isPlaying = true;
        clip.ended = false;
      }
        if (clip.windowVolume) {
          clip.volume = clip.volume || 1.0
          setTimeout(() => {
            let audio = document.getElementById(`audio-instance-${clip.name}`);
            if (audio) {
             
              audio.volume = windowToggle ? clip.volume : clip.volume / 2
            }
          }, 50)
        }
        return clip;
      }));
      
    },
    setAudioVolumnByName: (targetName, volume) => {
      setAudioCollection(audioCollection.map((clip, idx) => {
        if (clip.name === targetName){
          clip.volume = volume;
          if (clip.windowVolume) {
            setTimeout(() => {
              let audio = document.getElementById(`audio-instance-${clip.name}`);
              if (audio) {
                audio.volume = windowToggle ? clip.volume : clip.volume / 2
              }
            }, 50)
          } else {
            setTimeout(() => {
              let audio = document.getElementById(`audio-instance-${clip.name}`);
              if (audio) {
                audio.volume = volume
              }
            }, 50)          
          }
        } 
        return clip;
      }));
      
    },

    pauseAudioByName: (targetName) => {
      setAudioCollection(audioCollection.map((clip, idx) => {
        if (clip.name === targetName) clip.isPlaying = false;
        return clip;
      }));
    },

    removeAudio: (targetName) => {
      setAudioCollection(audioCollection.map((clip,idx) => {
        if (clip.name === targetName) clip.isPlaying = false;
        return clip;
      }))
    },
    removeAllAudio: () => {
      setAudioCollection(audioCollection.map((clip,idx) => {
        clip.isPlaying = false;
        return clip;
      }))
    },
    audioEnded: (targetName) => {
      setAudioCollection(audioCollection.map((clip,idx) => {
        if (clip.name === targetName) clip.ended = true;
        return clip;
      }))
    },
    audioRestarted: (targetName) => {
      setAudioCollection(audioCollection.map((clip,idx) => {
        if (clip.name === targetName) {
          clip.ended = false;
          clip.autoplay = true;
        }
        return clip;
      }))
    },
    toggleWindowVolume: () => {
      const newWindowToggle = !windowToggle
     
      setWindowToggle(newWindowToggle);
      setAudioCollection(audioCollection.map((clip,idx) => {
        if (clip.windowVolume && !newWindowToggle){ // window is going from open to close
          let audio = document.getElementById(`audio-instance-${clip.name}`);
        
          if (audio) {
            clip.volume = audio.volume;
            audio.volume = clip.volume/2;

          }         
        } else if (clip.windowVolume) { // window is going from closed to open
          let audio = document.getElementById(`audio-instance-${clip.name}`);
          if (audio) {
            audio.volume = clip.volume || 1;
          }
        }
        return clip;
      }))
    },
    setWindowVolumeInitial: () => {
      setWindowToggle(false);
      setAudioCollection(audioCollection.map((clip,idx) => {
        if (clip.windowVolume) {
          clip.volume = 1
          let audio = document.getElementById(`audio-instance-${clip.name}`);
          if (audio) {
            
            setTimeout(() => {
              audio.volume = clip.volume/2;
            }, 40);
          }
        } 
        return clip;
      }))
    },


    toggleAudioPlayStateByName: (targetAudio, targetName, isPlaying) => {
      // if (isPlaying) targetAudio.play();
      // else targetAudio.pause();

      // if (!isPlaying) audioController.pauseAudioByName(targetName);
      // else audioController.playAudioByName(targetName);

      // setAudioCollection(audioCollection.map((clip, idx) => {
      //   if (clip.name === targetName) clip.isPlaying = isPlaying;
      //   return clip;
      // }));
    }
  };
  
  
  const loadAudioData = async () => {
    const audioRes = await Axios.get('content/global-audio-collection.json');
    if (audioRes.status === 200) {
      setAudioCollection(audioRes.data.audio.map(audio => {
        audio.isPlaying = false;
        return audio;
      }));
    } else {
      console.log('JSON load error: ', audioRes);
      return null;
    }
  }

  /**
   * Load data function
   *
   */
  const loadData = async () => {

    try {
      const dataRes = await Axios.get(appData.modules[moduleIndex].source + '?t=' + Date.now());

      
      if (dataRes.status === 200) {
        setContentData(dataRes.data);
        console.log(dataRes.data);
       // console.log('data set')
      } else {
        console.log('JSON load error:', dataRes);
        return null;
      }

    } catch (e) {
      console.log(e);
    }
    return 
  };

  // Refresh content data, determine sequencer based on app.json
  const getSequencer = () => {
    // Check to see if content is still undefined
    if (contentData === undefined) {
      loadData();
      return <div className={`loading-screen`}><p>Loading...</p></div>;
    }
    
    if (audioCollection === undefined) {
        loadAudioData()
    }

    // Or if it returned null
    else if (contentData === null) {
      return <div className={`msg error`}><p>Data loading error. Check dev console for more information.</p></div>
    }

    // Or if data loaded
    else {
      // Get the correct sequencer
      if (controller.compareSequencerName('linear')) {
        return <Linear
          force={force}
          moduleIndex={moduleIndex}
          stepIndex={stepIndex}
          resetIndex={stepResetIndex}
          audioCollection={audioCollection}
          data={contentData}
          controller={controller}
          audioController={audioController}
          scoring={scoring}
          setScoring={setScoring}
          sharedData={sharedData}
          setSharedData={setSharedData}
          handleSubmitSurvey={handleSubmitSurvey}
        />
      }

      else if (controller.compareSequencerName('nonlinear')) {
        return <NonLinear
          force={force}
          moduleIndex={moduleIndex}
          resetIndex={stepResetIndex}
          data={contentData}
          audioCollection={audioCollection}
          controller={controller}
          audioController={audioController}
          scoring={scoring}
          setScoring={setScoring}
        />
      }
    }
  };

  /**
   * This is where the main content gets loaded (either menu or sequencer)
   * @returns {*}
   */
  const renderContent = () => {
    if (moduleIndex === -1) {
      // reset content to undefined to trigger load on index change
      if (contentData !== undefined) {
        setContentData(undefined);
      }

      return <Menu
        isDevEnvironment={isDevEnvironment}
        appData={appData}
        progress={moduleProgress}
        setModuleIndex={controller.setModuleIndex}
        data={globalData.menus.initial}
        scoring={scoring}
      />
    } else if (0 <= moduleIndex && moduleIndex < appData.modules.length) {
      return getSequencer();
    } else {
      return <div
        className="error"
        onClick={() => {
          setModuleIndex(-1);
        }}
      >
        Invalid moduleIndex <b>{moduleIndex}</b>. Click here to return to menu.
      </div>
    }
  };


  const isCourseComplete = () => {
    if (!moduleProgress.filter) return false; // this means something really goofed
    return moduleProgress.filter((el, i) => ((appData.modules[i].required !== false) && !el.complete)).length === 0;
  };

  const getTrackingComponent = () => {
    return <Tracking
      version={config.scorm.version || "scorm2004"}
      stepIndex={stepIndex}
      moduleIndex={moduleIndex}
      moduleProgress={moduleProgress}
      scoComplete={isCourseComplete()}
      onInit={onTrackingInit}
      scoScored={config.scorm.isScored || false}
      controller={controller}
    />
  };

  const onTrackingInit = (bookmark) => {
    // Set state to SCORM data if it exists
    console.log("SCORM data received by app.js: ", bookmark);

    if (bookmark.suspendData !== false) {
      setModuleProgress(bookmark.suspendData);
    }

    try {
      const parsed = JSON.parse(bookmark.lessonLocation);
      if (typeof parsed.moduleIndex === typeof 1) {
        setModuleIndex(parsed.moduleIndex);
      } else {
        setModuleIndex(initialModule);
      }
      if (typeof parsed.stepIndex === typeof 1) {
        setStepIndex(parsed.stepIndex)
      } else {
        setStepIndex(initialStep);
      }
    } catch (e) {
      console.log("Tracking init Error", e);
      return;
    }
  };

  // React useEffect to watch module and step index updates
  useEffect(() => {
    // whenever module index changes, reset step index
    if (!isDevEnvironment) setStepIndex(0);
  }, [moduleIndex]);

  useEffect(() => {
    if (isDevEnvironment) console.log(`step-${stepIndex} module-${moduleIndex}`);

    jiraController.setJiraFile(`step-${stepIndex} ${(((contentData || {}).steps || [])[stepIndex] || {}).type || ''}`);
    jiraController.setJiraItem(`${(appData.modules[moduleIndex] || {}).jiraModID || ''} ${(appData.modules[moduleIndex] || {}).name || ''}`);
  }, [stepIndex, moduleIndex]);

  /**
   * The actual App element render
   */
  return (

    <div className={`App`}>

      { getTrackingComponent() }

      {/* Add jira tag to dev environment */}
      { isDevEnvironment &&
        <Jira
          jiraTag={config.jiraTag}
          file={jira.file}
          location={jira.location}
          item={jira.item} /> }

      {/* Add QA menu to dev environment */}
      { isDevEnvironment &&
        <QAMenu
          steps={contentData ? contentData.steps : []}
          modules={appData.modules}
          moduleIndex={moduleIndex}
          stepIndex={stepIndex}
          controller={controller}
          editMode={editMode} /> }



     <div className={`audio-library`}>
       {/*
        * decoupling audio elements from audio players so they can access the same audio files
        *
        * NOTE: there was a bug here where whenever the filtered array changed,
        * any audio elements whose indexes changed as a result would re-render
        * and restart. To circumvent this, we nix the filter, render inactive mp3's as <div>'s 
        * and render active mp3's as audio. This maintains their indexes and avoids unwanted re-renders.  
        */
        audioCollection && audioCollection
         // .filter((audio) => audio.isPlaying)
          .map((e,i) => {
            return e.isPlaying ? <audio 
                    key={i}
                    loop={e.loop}
                    autoPlay={e.autoplay}
                    controls={false}
                    onEnded={() => audioController.audioEnded(e.name)}
                    id={`audio-instance-${e.name}`}
                    src={e.file}
                    volume={e.volume}
                  />
                  : <div className={`inactive-audio ${e.name}`} />
            })
        }
        </div>

      {/* Just for audio testing */}
      {
        isDevEnvironment &&
        contentData !== undefined &&
        <div className={`AudioVisualizer`}>

          <h1>Audio Visualization for Development</h1>
          {audioCollection && audioCollection.filter(e => e.isPlaying).map((audio, idx) => {
            return (
              <div className={`audio-instance-${idx}-container`} key={idx}>
                <p>{audio.name} <small>Looping? {audio.loop ? 'true' : 'false'}</small></p>
              </div>
            )
          })}
          {!audioCollection && <div className={`msg`}><p>Sorry, no audio files found.</p></div>}
        </div>
      }

      <div className={`course`}>
        <NavBar goToMenu={controller.goToMenu} goTo={e => console.log(`pretending to navigate to ${e}..`)} moduleIndex={moduleIndex} data={globalData.menus.navBar} audioController={audioController} />
        <div className={`content`}>
          { renderContent() }
        </div>
      </div>

      { isDevEnvironment && editMode &&
        <Editor
          moduleIndex={moduleIndex}
          stepIndex={stepIndex}
          contentData={contentData}
          controller={controller} /> }

    </div>
  );
}